import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import type { FieldDefinitionDto, SelectFieldConfigDto } from '@hqo/forms-client';

import { FormDefinition } from 'store/booking-add-ons/types';
import { OrderedAddOnsList } from 'pages/booking-add-ons/components/ordered-add-ons-list';
import { RootState } from 'store/reducer';
import { TransactionDetails } from 'store/transactions/types';
import { readAddOnFormDefinition, markAddOnsAmending } from 'store/booking-add-ons/actions';
import { resolveHelixUploadUrl } from 'shared/utils/helix-upload-url';
import {
  selectAddOnFormDefinition,
  selectAddOnsCancelledAt,
  selectAddOnsOrderedAt,
  selectAddOnsOrderedPaymentKey,
} from 'store/booking-add-ons/selectors';
import { paymentIdentity } from 'store/api-client/add-on-payment-types';
import { CREATE_GIVE_UP_MS, useOrderedAddOnPaymentQuery } from 'data/queries/useOrderedAddOnPaymentQuery.hook';
import { addOnReceipt } from './add-on-receipt';
import { addOnOrderActions, isAddOnOrderGone } from './add-on-actions';
import { addOnStatus } from './add-on-status';
import { configSelector } from 'store/config/selectors';
import { hasBookingEnded } from 'components/upcoming-bookings/hooks/usePastBookings';
import { useAddOnsEnabled } from 'hooks/use-add-ons-enabled.hook';
import { useCancelAddOnOrderMutation } from 'data/mutations/useCancelAddOnOrderMutation.hook';
import { IntlShape, useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';

import { CollapsableBlock } from 'components/collapsable-block';
import { AddOnPayment } from 'components/add-on-payment/add-on-payment';
import { PaymentResult } from 'components/add-on-payment/types';

import { AddOnCancelFailure } from 'store/api-client/add-on-cancel-types';
import { Alert, AlertDescription, Button } from '@hqo/ui-atoms';

import {
  AnswerLabel,
  AnswerRow,
  AnswerValue,
  CancelConfirmButtonsRow,
  CancelConfirmSection,
  CancelConfirmText,
  CancelOrderButton,
  OrderFooterRow,
} from './styles';

interface OrderedAddOnsProps {
  transaction: TransactionDetails;
  /**
   * Where "Modify add-ons" goes -- the same destination the first-time offer uses, since the
   * picker works out it is an amend by finding an order already on the booking.
   *
   * Optional, and its absence hides the control: two of this component's three render sites sit
   * inside surfaces with nowhere to push to.
   */
  onModifyAddOns?: (bookingId: string) => void;
}

/*
 * ONE answer, for two callers: this section renders the vendor rows, and the page hides them from the
 * receipt's payment details because of it. Written separately the two could drift apart and show the
 * order twice, or nowhere.
 *
 * `add_ons_enabled` is the only gate. helix emits `vendor_add_ons` for any booking that has them --
 * the rows can only exist if the submit path allowed them -- so whether to SHOW the section is this
 * app's decision alone.
 */

/*
 * How long the card still has to wait on an amend, in ms, or zero. Shared by the mount value and the
 * effect that schedules the timer, so the two cannot answer differently -- asking only whether an
 * order exists started the withhold on a window that had already closed, and a remount minutes after
 * an amend then painted one frame with the money withheld before the effect corrected it.
 *
 * The flash itself is not reachable from a test: `render` runs inside `act`, which flushes the effect
 * before anything can assert, so a spec sees only the corrected state and passes either way. The
 * guard here is that both readers call this, which the compiler enforces.
 */
const amendWindowRemaining = (orderedAt: number | null): number =>
  orderedAt === null ? 0 : orderedAt + CREATE_GIVE_UP_MS - Date.now();

export const useOrderedAddOnsVisible = (transaction: TransactionDetails | undefined): boolean => {
  const addOnsEnabled = useAddOnsEnabled();

  return Boolean(addOnsEnabled && transaction?.details?.vendor_add_ons?.length);
};

/* The answers map is keyed by field key and carries no question text; the definition supplies it. */
const fieldsByKey = (definition: FormDefinition | null | undefined): Map<string, FieldDefinitionDto> =>
  new Map(
    (definition?.steps ?? [])
      .flatMap(step => step.sections ?? [])
      .flatMap(section => section.fields ?? [])
      .map(field => [field.key, field]),
  );

/*
 * One stored part in the words the tenant chose it by. A select stores its option VALUES, which are
 * keys rather than copy, and the definition -- read in the reader's language -- holds the label for
 * each. A checkbox stores a boolean, which printed raw is the English word "true".
 *
 * ⚠️ An option the template no longer has keeps its stored value: it is still what was ordered, and
 * a gap would read as an unanswered question. `floor_suites` selects carry no options in the
 * definition at all (they resolve at fill time), so theirs stay as stored too.
 */
const answerPart = (part: string | number | boolean, field: FieldDefinitionDto, intl: IntlShape): string => {
  if (typeof part === 'boolean') {
    return intl.formatMessage({ id: part ? 'yes' : 'no' });
  }

  if (field.type === 'select') {
    /*
     * ⚠️ `| undefined` in the cast, against a DTO that declares `config` required. Generated
     * required-ness is a promise about the service, not a guarantee about the row, and this runs
     * inside the booking detail's `useMemo` -- a definition whose select arrived without a config
     * would take the whole page down for an answer-label nicety. The stored value then prints as
     * stored, which is the same fallback an unknown option already gets.
     */
    const options = (field.config as SelectFieldConfigDto | undefined)?.options ?? [];

    return options.find(option => option.value === String(part))?.label ?? String(part);
  }

  return String(part);
};

/*
 * Primitives and lists of them only. A file upload answers as objects, and `String(value)` on one
 * prints "[object Object]" at a tenant, so anything else is left out rather than rendered badly.
 */
const formatAnswer = (value: unknown, field: FieldDefinitionDto, intl: IntlShape): string | null => {
  const raw = Array.isArray(value) ? value : [value];
  const parts = raw.filter(
    (part): part is string | number | boolean =>
      typeof part === 'string' || typeof part === 'number' || typeof part === 'boolean',
  );

  /* All or nothing: a partly-rendered answer is worse than an absent one. */
  return parts.length && parts.length === raw.length
    ? parts.map(part => answerPart(part, field, intl)).join(', ')
    : null;
};

/*
 * RES-1316 - the add-ons a tenant ordered on this booking, read back.
 *
 * ⛔ ITS OWN BLOCK, NEVER THE BOOKING'S PAYMENT DETAILS. Vendor add-ons are billed by their provider
 * and never enter `total_price`, so showing them inside the figures that reconstruct the room charge
 * reads as though the tenant paid the room for them. The receipt's payment block is the BOOKING's.
 *
 * ⚠️ And relying on that block as a fallback does not work anyway: it is gated on
 * `total_price`, so on a free room -- a booking that can still carry a $50 add-on -- it does not
 * render at all and the order appears nowhere. That is why this is mounted on all three surfaces
 * rather than only on the detail page.
 *
 * ⭐ BUILT OFF BOTH SURFACES, AND THE SPLIT IS THE POINT. helix answers what was ORDERED — the rows,
 * the party, and (since #11165) its logo — because that is what the booking read already carries and
 * what survives whatever happened to the charge. resource-booking-service's `/add-ons` answers what
 * happened to the MONEY, because only it holds the link row to the work order. Neither can answer
 * the other's half: helix has never seen a checkout session, and the add-ons read resolves no
 * company name. Reading one and inferring the other is what left this section stating no total.
 *
 * ⚠️ THE MONEY HALF IS ADDITIVE AND MUST FAIL THAT WAY. The payment read sits behind
 * `CAN_MANAGE_TENANT_WORK_ORDERS` and can 403, and the service answers a null payment when
 * work-orders is unreachable. The rows are helix's and are already on screen by then, so a missing
 * payment block costs the figures, never the section — the same rule helix applies on its own side.
 *
 * ⚠️ NO LOGO ON THE FLOW'S CONFIRMATION SCREEN, and that is a gap upstream rather than drift here.
 * It renders from the catalog shape, which carries `owning_party_name` and no logo at all; only
 * helix's booking read resolves the company. Both screens fall back to the same initials disc, so
 * the asymmetry is a logo appearing here and not there, not two different treatments.
 */
export const OrderedAddOns = ({ transaction, onModifyAddOns }: OrderedAddOnsProps): JSX.Element | null => {
  const intl = useIntl();
  const locale = useLocale();
  const dispatch = useDispatch();
  const isVisible = useOrderedAddOnsVisible(transaction);
  /*
   * Asked of `hasBookingEnded` rather than a local comparison so this card and the tile beside it
   * cut at the same minute. It withholds the controls that would change the order
   * -- amend, cancel, reorder -- not the card. Paying stays available: an unpaid balance on a
   * delivered order has no other route to its checkout session.
   */
  const hasEnded = hasBookingEnded(transaction?.details?.end_at);

  const { apiUrl, buildingUuid } = useSelector(configSelector);
  const vendorAddOns = transaction?.details?.vendor_add_ons;
  /* One party per booking, so one response, stored identically on each of its rows. */
  const formResponse = vendorAddOns?.find(addOn => addOn.form_response)?.form_response ?? null;
  const templateUuid = formResponse?.form_template_uuid ?? null;
  const definition = useSelector((state: RootState) =>
    selectAddOnFormDefinition(state, templateUuid, locale as string),
  );

  /*
   * ⚠️ Gated on `isVisible`, not just on having a template. A building with the feature off can still
   * hold vendor rows from before it was turned off, so without this the section fetches a form
   * definition to label answers it then renders nothing of.
   */
  useEffect(() => {
    if (isVisible && templateUuid && definition === undefined) {
      dispatch(readAddOnFormDefinition.request({ templateUuid, lang: locale as string }));
    }
  }, [dispatch, isVisible, templateUuid, definition, locale]);

  /*
   * ⚠️ `details.id` is the booking id the add-ons read is keyed on — the same one the add-on step
   * passes — not the transaction's own `id`. Coerced here because helix types it `string | number`,
   * and withheld rather than passed as `NaN` if it ever arrives unparseable.
   */
  const bookingId = Number(transaction?.details?.id);
  const paymentBookingId = Number.isFinite(bookingId) ? bookingId : null;
  /*
   * RES-1367 — WHEN billing reported a charge, and WHEN the tenant left the sheet. Timestamps rather
   * than flags: the read below measures its settle window from them, so a second attempt on the same
   * booking opens a second window instead of inheriting an expired one. See the hook's parameters.
   */
  const [chargedAt, setChargedAt] = useState<number | null>(null);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  /* When the service accepted a cancel made from this card. */
  const [localCancelledAt, setLocalCancelledAt] = useState<number | null>(null);
  /*
   * When an order was placed against this booking. Held in the store rather than in state because
   * it has to outlive this component: the add-on dialog's route is a sibling of the receipt's (see
   * `payment-content.tsx`), so placing an order unmounts this tree and remounts it with every
   * `useState` back at null -- and the card would then read for a payment block that does not
   * exist yet and cache the null.
   */
  const orderedAt = useSelector((state: RootState) => selectAddOnsOrderedAt(state, transaction?.details?.id));
  /* The money as it stood before that order went in. Drives the withholding below, and the poll. */
  const orderedPaymentKey = useSelector((state: RootState) =>
    selectAddOnsOrderedPaymentKey(state, transaction?.details?.id),
  );
  /*
   * A cancel made from the add-on dialog, in the store for the same reason `orderedAt` is:
   * "Remove all add-ons" lives on a sibling route, so pressing it tears this card down.
   */
  const cancelledFromDialogAt = useSelector((state: RootState) =>
    selectAddOnsCancelledAt(state, transaction?.details?.id),
  );
  /*
   * The later of the two, not a fallback between them: both can be stamped in one visit, and the
   * window is only worth measuring from whichever happened last.
   */
  const cancelledAt = Math.max(localCancelledAt ?? 0, cancelledFromDialogAt ?? 0) || null;
  const { data: orderedRead } = useOrderedAddOnPaymentQuery(
    buildingUuid ?? null,
    paymentBookingId,
    isVisible,
    chargedAt,
    dismissedAt,
    cancelledAt,
    orderedAt,
    orderedPaymentKey,
  );
  /* The money and the amend gates arrive together, off one GET. */
  const fetched = orderedRead?.payment ?? null;
  /*
   * Closes the withhold below when the poll that would clear it gives up. An unchanged fingerprint
   * does NOT imply the server is still working -- an approval-gated amend, a quote added to an
   * unpriced order and a free-line quantity change all leave every field as it was -- so without a
   * bound the card states "awaiting price" over a correct figure for the rest of the session.
   *
   * A timer rather than reading the clock at render: nothing re-renders this card when the window
   * simply elapses, so the last poll could leave the withhold latched on.
   */
  const [isAmendSettling, setIsAmendSettling] = useState(() => amendWindowRemaining(orderedAt) > 0);

  useEffect(() => {
    const remaining = amendWindowRemaining(orderedAt);

    setIsAmendSettling(remaining > 0);

    if (remaining <= 0) {
      return undefined;
    }

    const timer = setTimeout(() => setIsAmendSettling(false), remaining);

    return () => clearTimeout(timer);
  }, [orderedAt]);

  /*
   * At render, never inside the query. This card's query is already active when an amend is
   * submitted, so the invalidation fires in the same tick as the dispatch recording the baseline and
   * the refetch runs on a closure where it is still null -- disarmed exactly when it is needed. Read
   * from the store, it cannot be stale here however the fetch was scheduled. (The confirmation's
   * query escapes this only because it is disabled until that same effect enables it.)
   *
   * A refusal stands it down at once: work-orders wrote nothing, so the money on hand is current and
   * waiting on it would be waiting for a change that is never coming.
   */
  const isRepricing =
    isAmendSettling &&
    !orderedRead?.amendRefusedReason &&
    orderedPaymentKey !== null &&
    paymentIdentity(fetched) === orderedPaymentKey;
  /*
   * The block itself stays. Only the RECEIPT is withheld, below.
   *
   * Nulling it here was the first shape and it took the order's existence with the figure: `null` is
   * already this read's wire for "we were told nothing" -- a 403, an unreachable work-orders -- so
   * `addOnOrderActions` answered NOTHING and Cancel, Pay, Amend and Order-again all vanished for the
   * length of the withhold, the status pill with them. The tenant could not stand down an order they
   * had just changed. Withholding the figure is the ask; withholding the order is not.
   */
  const payment = fetched;

  /*
   * ⚠️ Each line is labelled in the currency its OWN price was stored in (helix #11179), then the work
   * order's, and only then the booking's. Rows ordered before helix stored one carry none, and the
   * booking's is null on a free room -- which can still carry a paid add-on -- so labelling by the
   * booking alone withheld every line amount on exactly the orders whose total was stated beside them.
   */
  const lineCurrency = payment?.currency ?? transaction?.currency_type;
  /*
   * work-orders is the only side that knows whether a zero was priced, and after a quote the only
   * side holding the figure: helix still has the zero it stored at selection.
   */
  const vendorLines = orderedRead?.lines;
  const vendorLineByItem = useMemo(
    /* Lower-cased on both sides: RBS normalises these before matching and helix's rows do not. */
    () => new Map((vendorLines ?? []).map(line => [line.catalogItemUuid.toLowerCase(), line])),
    [vendorLines],
  );

  const lines = useMemo(
    () =>
      (vendorAddOns ?? []).map(addOn => {
        const vendorLine = vendorLineByItem.get(addOn.catalog_item_uuid.toLowerCase());
        const isConfirmed = vendorLine?.confirmedWithVendor === true;

        return {
          /*
           * ⛔ `catalog_item_uuid`, NOT `uuid`, and it is the amend that forces the choice. RES-1374
           * makes this list MUTABLE -- work-orders #868/#871 rebuild a vendor's items from the whole
           * desired selection -- so the key has to identify the same add-on across two reads of a
           * list whose rows move. `uuid` is the ORDERED ROW's own id, minted fresh by the rebuild,
           * which makes every surviving row look new and React discard and rebuild all of them.
           * `catalog_item_uuid` is the add-on itself and survives, and #868's `assertDistinctItems`
           * rejects a selection that names one twice, so it is unique by construction.
           *
           * ⚠️ SNAKE CASE because this is `VendorAddOnDto`, THIS APP'S OWN read-back shape, not the
           * generated `BookingAddOnLineDto` -- the two describe the same row and disagree about
           * casing, which is the whole reason to name the source rather than the field. The generated
           * one is also where 1.1.61 made `uuid` optional (RBS #191, deliberate -- "I can find no
           * consumer of this field"); a `uuid` key would have been wrong on both shapes for two
           * unrelated reasons.
           */
          key: addOn.catalog_item_uuid,
          name: addOn.name,
          /*
           * Price and quantity come from the vendor's line TOGETHER, never one of each. A refused
           * amend leaves helix holding five against the vendor's two, so pairing their unit price
           * with helix's count states a figure neither system holds.
           */
          quantity: isConfirmed ? vendorLine?.quantity ?? addOn.quantity : addOn.quantity,
          price: isConfirmed ? vendorLine.price : addOn.price,
          /*
           * UNGATED, unlike the three fields above, and the asymmetry is deliberate. Those choose
           * between two different figures and need to know which side the figure came from. This one
           * does not: RBS computes `priceKnown` correctly on an UNCONFIRMED line too, from helix's
           * own `price > 0`, so gating it discarded a right answer and fell back to helix's zero --
           * which rendered a quote-required line as a currency zero in exactly the window after an
           * order is placed, when `reconcileLines` has no work-order items yet and confirms nothing.
           * That is the defect this ticket exists to remove, so the gate had to go (RES-1317 #1980
           * found the same thing from the other side).
           *
           * The cost is known and accepted: an unconfirmed genuinely-free add-on reads "Price on
           * request", because helix marks every zero unpriced and cannot tell the two apart. That is
           * master's behaviour too. RES-1317 measured it at zero occurrences over seven days; that
           * figure is theirs and is recorded here as reported, not as something re-measured.
           */
          priceKnown: vendorLine?.priceKnown,
          /*
           * The vendor's line carries no currency of its own, so a confirmed line takes the work
           * order's -- which is what `lineCurrency` resolves first. Helix's stored currency is the
           * selection's, and labelling the vendor's figure with it prints the wrong symbol.
           */
          currency: isConfirmed ? lineCurrency : addOn.currency ?? lineCurrency,
          imageUrl: addOn.image_url,
          imageAltText: addOn.image_alt_text,
        };
      }),
    [vendorAddOns, lineCurrency, vendorLineByItem],
  );

  /*
   * Pay later: an order placed without paying keeps an open checkout session on its work order, and this
   * is the only place a tenant comes back to it. `isPayable` is the service's answer to "will billing
   * still take a charge against that session" -- a settled, refunded, declined, expired or not-yet-priced
   * order answers false, so the control never offers a payment the widget would refuse.
   */
  const queryClient = useQueryClient();
  /*
   * RES-1367: what the tenant may DO about this order, decided in one place and tested without
   * rendering -- the same arrangement as `addOnReceipt` above, which decides what the money means.
   *
   * "Modify" is one control because RBS #192 amends an existing work-order request in place:
   * re-submitting the selection to helix replaces the `resource_booking_add_ons` rows and the RBS
   * worker calls `AmendRequestsForSelection`. Where there is no order left to amend the action is
   * `reorder` instead.
   *
   * Whether it may be amended is asked at two levels -- `canOrderAddOns` for the booking,
   * `amendRefusedReason` for this work order -- so the whole read goes in, not just the payment.
   */
  const { actions, isVendorUnderway } = useMemo(
    () =>
      addOnOrderActions({
        payment,
        canOrderAddOns: Boolean(orderedRead?.canOrderAddOns),
        amendRefusedReason: orderedRead?.amendRefusedReason ?? null,
      }),
    [payment, orderedRead?.canOrderAddOns, orderedRead?.amendRefusedReason],
  );
  /*
   * `isVendorUnderway` comes from the same call that decided the controls, so the pill and the
   * footer cannot disagree about whether the vendor has started.
   */
  const status = useMemo(() => addOnStatus(payment ?? null, isVendorUnderway), [payment, isVendorUnderway]);
  /*
   * Not while the money is being withheld. The session on hand belongs to the order as it was, so
   * offering it would take a payment for a figure this card is deliberately refusing to state.
   */
  const canPay = actions.includes('pay') && !isRepricing;
  /*
   * Both halves: `addOnOrderActions` says whether the service will take the change,
   * `onModifyAddOns` whether this surface has anywhere to send the tenant. The receipt panes
   * render this component with no router beneath them.
   */
  const canAmend = actions.includes('amend') && Boolean(onModifyAddOns) && paymentBookingId !== null && !hasEnded;
  /*
   * Offered alongside amend rather than hidden behind it: reaching a cancel through "Modify add-ons"
   * and then "Remove all add-ons" is a route nobody finds. Both controls still read the service's
   * own verdict from `addOnOrderActions`, so the two entry points cannot disagree about whether a
   * cancel is allowed.
   */
  const canCancel = actions.includes('cancel') && !hasEnded;
  /*
   * "Order again" is a separate action from `amend` because the service would take an amend on a
   * cancelled link -- `amendRefusedReason` comes back null -- and there is nothing to modify. Same
   * surface-level clauses as `canAmend`.
   */
  const canReorder = actions.includes('reorder') && Boolean(onModifyAddOns) && paymentBookingId !== null && !hasEnded;
  const payableSessionUuid = canPay ? payment?.checkoutSessionUuid ?? null : null;
  /*
   * ⛔ The session the OPEN sheet was handed, held apart from `payableSessionUuid`. The widget renders its
   * own success screen inside the still-open overlay, and a re-read that lands meanwhile flips `isPayable`
   * to false; deriving the sheet from the live answer would unmount it and tear that screen away from a
   * tenant who has just paid. So the sheet lives while this is set, and only closing clears it.
   */
  const [openSessionUuid, setOpenSessionUuid] = useState<string | null>(null);
  const onPayNow = useCallback(() => setOpenSessionUuid(payableSessionUuid), [payableSessionUuid]);
  /*
   * ⛔ BILLING SAID THE MONEY MOVED. The strongest signal there is, and the only one that distinguishes
   * "not paid yet" from "paid, not recorded yet" — the read answers `pending_payment` to both.
   *
   * ⚠️ IT DOES NOT CLOSE THE SHEET, and must not. The widget renders its own success screen inside the
   * still-open overlay; the tenant closes that themselves, and `onPaymentOpenChange` below is still
   * the single exit.
   */
  const onPaymentComplete = useCallback((result: PaymentResult) => {
    if (result.success) {
      setChargedAt(Date.now());
    }
  }, []);
  /*
   * ⚠️ Closing is the single exit, for a payment made, declined or abandoned alike (see AddOnPayment), so
   * the payment block is re-read on every close: only work-orders knows how the session ended.
   *
   * ⛔ THAT ONE RE-READ IS NOT ENOUGH ON ITS OWN, and believing it was is the whole of the bug. It
   * fires the instant the sheet closes, which is seconds before work-orders records the charge — so it
   * reliably fetches the stale answer, caches it, and leaves the block saying "awaiting payment" on an
   * order that is paid until the tenant leaves the booking and comes back. The timestamp is what opens
   * the bounded window that keeps asking; the invalidation stays because it is the first look, and on
   * a declined or abandoned sheet it is the only one that matters.
   */
  const onPaymentOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        return;
      }

      setOpenSessionUuid(null);
      setDismissedAt(Date.now());
      queryClient.invalidateQueries({ queryKey: ['ordered-add-on-payment', buildingUuid ?? null, paymentBookingId] });
    },
    [queryClient, buildingUuid, paymentBookingId],
  );

  /*
   * The same destination the first-time offer uses -- no amend route and no mode flag in the URL,
   * because the picker works out it is an amend by finding an order already on the booking, which
   * cannot go stale between the push and the arrival.
   */
  const onModifyOrder = useCallback(() => {
    if (paymentBookingId !== null) {
      /*
       * The money this card is showing, recorded before the route takes the card down, so the picker
       * can tell a server that has caught up from one that has not. Dispatched rather than held
       * locally for the same reason `lastOrdered` is: this is fire-and-navigate and the tree unmounts.
       */
      dispatch(
        markAddOnsAmending({
          bookingId: String(paymentBookingId),
          /*
           * Null, never `paymentIdentity(null)`: that returns the string 'none', which would arm the
           * withhold against any later read whose block is absent -- a swallowed 403, an unreachable
           * work-orders -- and hold money back on an answer that never had one.
           *
           * Defensive rather than live, and deliberately untested: `addOnOrderActions` answers
           * NOTHING on a null block, so this control cannot render to be pressed. The guard costs a
           * ternary and the sentinel it avoids is the expensive kind.
           */
          paymentKey: fetched ? paymentIdentity(fetched) : null,
        }),
      );
      onModifyAddOns?.(String(paymentBookingId));
    }
  }, [dispatch, fetched, onModifyAddOns, paymentBookingId]);

  /*
   * Stand the vendor order down, keeping the room booked. The outcome is read off the resolved
   * value, never `isError`: the mutation does not reject (see the hook), so `isSuccess` would be
   * true on a refusal. The three failure kinds are kept apart because they need different copy --
   * `refused` is permanent, `unavailable` means the order is still being created and a retry works.
   */
  const { mutateAsync: cancelAddOnOrder, isPending: isCancelling } = useCancelAddOnOrderMutation();
  const [cancelFailure, setCancelFailure] = useState<AddOnCancelFailure | null>(null);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const onCancelOrder = useCallback(async () => {
    if (!buildingUuid || !paymentBookingId || isCancelling) {
      return;
    }

    setCancelFailure(null);

    const outcome = await cancelAddOnOrder({ buildingUuid, bookingId: paymentBookingId });

    if (outcome.kind !== 'cancelled') {
      setCancelFailure(outcome.kind);
      /* The sentence replaces the question: it names the refusal, and the control is still there. */
      restoreCancelFocusRef.current = true;
      setIsCancelConfirmOpen(false);

      return;
    }

    setIsCancelConfirmOpen(false);

    /*
     * The 200 is not the settled state: RBS settles the links and publishes, and work-orders
     * stands the vendor down and refunds off that event. So this opens a window -- same shape as
     * the charge above -- and the invalidation below is only its first look.
     */
    setLocalCancelledAt(Date.now());
    queryClient.invalidateQueries({ queryKey: ['ordered-add-on-payment', buildingUuid ?? null, paymentBookingId] });
  }, [cancelAddOnOrder, queryClient, buildingUuid, paymentBookingId, isCancelling]);

  const openCancelConfirm = useCallback(() => setIsCancelConfirmOpen(true), []);
  /*
   * Stays put while the cancel is in flight: snapping back to the footer would say the order is
   * untouched. Both buttons take `aria-disabled`, never `disabled`, which drops focus to the body.
   */
  const closeCancelConfirm = useCallback(() => {
    if (isCancelling) return;
    restoreCancelFocusRef.current = true;
    setIsCancelConfirmOpen(false);
  }, [isCancelling]);

  /* Focus follows the question in and back out; not flagged on a success, where the footer goes too. */
  const cancelConfirmRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const restoreCancelFocusRef = useRef(false);
  useEffect(() => {
    if (isCancelConfirmOpen) {
      cancelConfirmRef.current?.focus();

      return;
    }

    if (restoreCancelFocusRef.current) {
      restoreCancelFocusRef.current = false;
      cancelButtonRef.current?.focus();
    }
  }, [isCancelConfirmOpen]);
  /* Whether the service now says the order is gone, whatever the cancel call answered. */
  const isOrderGone = isAddOnOrderGone(payment);
  /*
   * Derived rather than cleared in an effect: `setCancelFailure` fires once, at the start of the
   * next attempt, so a re-read landing afterwards and reporting the order cancelled would never
   * touch it -- leaving "we couldn't cancel these add-ons" standing beside a cancelled order.
   */
  const cancelFailureMessageId = cancelFailure && !isOrderGone ? `add_on_cancel_${cancelFailure}` : null;

  /*
   * One decision about what the money means, made in one place and tested without rendering: which
   * figure the total is, whether a tax line can be stated at all, and whether anything came back.
   */
  /* The one withhold: a receipt built from money that describes the previous order would state a
   * charge the tenant no longer owes. Everything else on the card reads the block as it came. */
  const receipt = useMemo(() => addOnReceipt(isRepricing ? null : payment), [payment, isRepricing]);

  /* Only keys the definition resolves: a republished template can leave an answer with no question. */
  const answers = useMemo(() => {
    const fields = fieldsByKey(definition);

    return Object.entries(formResponse?.values ?? {})
      .map(([key, value]) => {
        const field = fields.get(key);

        return { key, label: field?.label, answer: field ? formatAnswer(value, field, intl) : null };
      })
      .filter((entry): entry is { key: string; label: string; answer: string } => !!entry.label && !!entry.answer);
  }, [definition, formResponse, intl]);

  if (!isVisible) {
    return null;
  }

  const partyName = vendorAddOns?.find(addOn => addOn.catalog_company_name)?.catalog_company_name;
  /* One party per booking (RES-1051), so the first row that resolved one speaks for all of them. */
  const storedPartyLogo = vendorAddOns?.find(addOn => addOn.catalog_company_logo_url)?.catalog_company_logo_url;
  /*
   * ⛔ RESOLVED, NEVER RENDERED RAW. helix echoes `companies.logo_url` exactly as stored, and that
   * is a relative `uploads/…` storage path far more often than an absolute url — see
   * `resolveHelixUploadUrl`. Handed over raw it resolves against whatever origin this bundle is
   * served from, 404s there, and the disc quietly falls back to the initials. That is
   * indistinguishable from a vendor who set no logo at all, which is how it went unnoticed: the
   * field was populated and correct the whole time.
   */
  const partyLogoUrl = resolveHelixUploadUrl(storedPartyLogo, apiUrl);

  const content = (
    <>
      <OrderedAddOnsList
        lines={lines}
        partyName={partyName}
        partyLogoUrl={partyLogoUrl}
        status={status}
        tax={receipt.tax}
        total={receipt.total ?? undefined}
        charge={receipt.charge}
        refundedCents={receipt.refundedCents}
        /* Two unrelated reasons to withhold: nothing has priced the order, or what came back still
         * describes it as it was before the amend. Collapsing them would drop one of the two. */
        isAwaitingPrice={receipt.isAwaitingPrice || isRepricing}
        testIdPrefix="booking-details-add-ons"
        /*
         * VIS-2193: the thumbnail on every line, the image or the `Package` box (behind
         * `show-product-images`). The booking read sends no product image yet, so this is the box
         * until it does -- the same box the step and the dialog show for a product with no image.
         */
        withProductImages
      />

      {!!answers.length && (
        <div data-testid="booking-details-add-ons-answers">
          {/*
            RES-1367: FOLDABLE, and folded to begin with. What the tenant answered when they ordered
            is reference detail -- read once at submit, then in the way of the order above it, which
            is the part they came back for. The block supplies the heading, so this renders none of
            its own.
          */}
          <CollapsableBlock title={intl.formatMessage({ id: 'add_on_answers_heading' })} section>
            {answers.map(answer => (
              <AnswerRow key={answer.key}>
                <AnswerLabel>{answer.label}</AnswerLabel>
                <AnswerValue>{answer.answer}</AnswerValue>
              </AnswerRow>
            ))}
          </CollapsableBlock>
        </div>
      )}

      {/*
        RES-1367: the order's own FOOTER, below everything the order is -- the rows, the figures and
        whatever was asked at the time -- rather than wedged between the figures and the questions,
        where it split one record in half.

        Same shape as the booking card's actions row one card above: 20px of air, then the action at
        the trailing edge, and NO rule -- see `OrderFooterRow`. The push belongs to the button, not
        to the row, for the same reason it does there: a row that said `space-between` would put a
        lone button at the START.
      */}
      {/*
          The text has to go in `AlertDescription`: Alert lays itself out as `grid-cols-[0_1fr]`,
          where column one is a zero-width icon slot, so a raw child renders into 0px.
      */}
      {cancelFailureMessageId && (
        <Alert variant="destructive" data-testid="booking-details-add-ons-cancel-error">
          <AlertDescription>{intl.formatMessage({ id: cancelFailureMessageId })}</AlertDescription>
        </Alert>
      )}

      {/* RES-1367: cancelling refunds, so it asks first -- in place of the footer, never a modal. */}
      {isCancelConfirmOpen && canCancel ? (
        <CancelConfirmSection ref={cancelConfirmRef} tabIndex={-1}>
          <CancelConfirmText data-testid="booking-details-add-ons-cancel-prompt">
            {/* The flag, not `receipt.charge`: a nullable figure would drop the promise on a real refund. */}
            {intl.formatMessage({
              id: payment?.isCancellableWithRefund ? 'add_on_cancel_confirm_refund' : 'add_on_cancel_confirm',
            })}
          </CancelConfirmText>
          <CancelConfirmButtonsRow>
            <Button
              type="button"
              variant="outline"
              onClick={closeCancelConfirm}
              aria-disabled={isCancelling}
              data-testid="booking-details-add-ons-cancel-keep"
            >
              {intl.formatMessage({ id: 'keep_request' })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onCancelOrder}
              aria-disabled={isCancelling}
              aria-busy={isCancelling}
              data-testid="booking-details-add-ons-cancel-confirm"
            >
              {intl.formatMessage({ id: 'yes_cancel' })}
            </Button>
          </CancelConfirmButtonsRow>
        </CancelConfirmSection>
      ) : (
        (payableSessionUuid || canCancel || canAmend || canReorder) && (
          <OrderFooterRow>
            {/* Bare and carrying no push: only the trailing button may ask -- see CancelOrderButton. */}
            {payableSessionUuid && (
              <Button type="button" variant="default" onClick={onPayNow} data-testid="booking-details-add-ons-pay-now">
                {intl.formatMessage({ id: 'add_on_pay_now' })}
              </Button>
            )}

            {/*
              "Order again" on an order the service says is gone; `addOnOrderActions` emits this or
              "Modify add-ons", never both. The picker arrives prefilled because re-submitting an
              unchanged selection is what bumps helix's `updated_at`, and RBS's revive check reads
              that bump to tell a genuine new order from a stale echo of the cancelled one.
          */}
            {canReorder && (
              <Button
                type="button"
                variant="secondary"
                onClick={onModifyOrder}
                data-testid="booking-details-add-ons-reorder"
              >
                {intl.formatMessage({ id: 'add_on_reorder' })}
              </Button>
            )}

            {canAmend && (
              <Button
                type="button"
                variant="secondary"
                onClick={onModifyOrder}
                data-testid="booking-details-add-ons-modify"
              >
                {intl.formatMessage({ id: 'add_on_modify_order' })}
              </Button>
            )}

            {canCancel && (
              <CancelOrderButton
                ref={cancelButtonRef}
                type="button"
                variant="secondaryDestructive"
                onClick={openCancelConfirm}
                data-testid="booking-details-add-ons-cancel"
              >
                {intl.formatMessage({ id: 'add_on_cancel_order' })}
              </CancelOrderButton>
            )}
          </OrderFooterRow>
        )
      )}
    </>
  );

  return (
    <div data-testid="booking-details-add-ons">
      {/*
        ⭐ EXPANDED BY DEFAULT, unlike every other collapsible on these surfaces. They hold reference
        detail a tenant goes looking for; this holds what they just ordered and were charged for, and
        a collapsed one reads as the order being missing — which is the complaint this section exists
        to answer. Collapsible so it can be folded away on a booking the tenant is only passing
        through, not so it starts hidden.
      */}
      <CollapsableBlock title={intl.formatMessage({ id: 'add_ons' })} card defaultExpanded>
        {content}
      </CollapsableBlock>

      {/*
        ⛔ OUTSIDE THE COLLAPSIBLE, DELIBERATELY. `CollapsableBlock` renders `{isInfoExpanded &&
        children}` -- it UNMOUNTS what it folds away, it does not hide it -- so a payment mounted
        inside it is destroyed the moment the section is collapsed, taking a half-entered card or a
        result screen with it. Re-expanding then builds a SECOND widget against the same session,
        because the open session is held in state out here and never knew.

        The widget already guards its own identity against exactly this (`createPaymentWidgetRemote`
        is memoised once so a re-render cannot remount it) -- a guard a parent unmount walks
        straight through.

        Nothing moves on screen: it is a federated remote that portals its own Dialog/Drawer to
        `document.body` and draws nothing where it sits. Whether a tenant can reach the heading
        behind that overlay is billing-admin's modality to decide, which is the other half of why
        this must not depend on it.
      */}
      {openSessionUuid && (
        <AddOnPayment
          checkoutSessionUuid={openSessionUuid}
          open
          onOpenChange={onPaymentOpenChange}
          onPaymentComplete={onPaymentComplete}
        />
      )}
    </div>
  );
};
